import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import apiClient from '@/lib/apiClient'
import type { BankAccount, BankAccountFormData } from '@/types/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  CreditCard,
  Globe,
  Loader2,
  Pencil,
  Plus,
  Receipt,
  Save,
  Settings as SettingsIcon,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface Setting {
  id: string
  key: string
  value: unknown
  type: string
  description: string
  updated_at: string
}

// Define which keys belong to which tab
const GENERAL_KEYS = [
  'site_title',
  'store_name',
  'store_address',
  'store_phone',
  'store_whatsapp',
  'store_email',
  'store_hours',
  'store_latitude',
  'store_longitude',
]
const ORDER_KEYS = ['tax_rate']
const SOCIAL_KEYS = ['social_instagram', 'social_facebook', 'social_x', 'social_youtube']

const initialBankAccountForm: BankAccountFormData = {
  name: '',
  account_number: '',
  bank_name: '',
  is_active: true,
}

export default function SettingsPage() {
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)

  // Bank Account state
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null)
  const [bankAccountToDelete, setBankAccountToDelete] = useState<BankAccount | null>(null)
  const [bankAccountForm, setBankAccountForm] =
    useState<BankAccountFormData>(initialBankAccountForm)

  // State for form values
  const [settings, setSettings] = useState<Record<string, string>>({
    tax_rate: '',
    store_name: '',
    store_address: '',
    store_phone: '',
    store_whatsapp: '',
    store_email: '',
    store_latitude: '',
    store_longitude: '',
    store_hours: '',
    social_instagram: '',
    social_facebook: '',
    social_x: '',
    social_youtube: '',
    site_title: '',
  })

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiClient.getSettings(),
  })

  // Bank Accounts Query
  const { data: bankAccountsResponse, isLoading: isLoadingBankAccounts } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => apiClient.getBankAccounts(),
  })

  // Update state when settings are loaded
  useEffect(() => {
    if (settingsResponse?.data) {
      setSettings((prev) => {
        const newSettings = { ...prev }
        settingsResponse.data.forEach((s: Setting) => {
          if (Object.prototype.hasOwnProperty.call(newSettings, s.key)) {
            newSettings[s.key] = String(s.value)
          }
        })
        return newSettings
      })
    }
  }, [settingsResponse])

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      apiClient.updateSetting(key, value),
  })

  // Bank Account Mutations
  const createBankAccountMutation = useMutation({
    mutationFn: (data: BankAccountFormData) => apiClient.createBankAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      toast.success('Bank account created successfully')
      handleCloseDialog()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create bank account')
    },
  })

  const updateBankAccountMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BankAccountFormData> }) =>
      apiClient.updateBankAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      toast.success('Bank account updated successfully')
      handleCloseDialog()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update bank account')
    },
  })

  const deleteBankAccountMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteBankAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      toast.success('Bank account deleted successfully')
      setIsDeleteDialogOpen(false)
      setBankAccountToDelete(null)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete bank account')
    },
  })

  const handleSaveTab = async (keys: string[]) => {
    setIsSaving(true)

    try {
      // Validate tax rate if present
      if (keys.includes('tax_rate')) {
        const numValue = parseFloat(settings.tax_rate)
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          toast.error('Tax rate must be between 0 and 100')
          setIsSaving(false)
          return
        }
      }

      // Save all settings in this tab
      for (const key of keys) {
        let value: unknown = settings[key]

        // Convert numeric fields
        if (key === 'tax_rate' || key === 'store_latitude' || key === 'store_longitude') {
          value = parseFloat(settings[key]) || 0
        }

        await updateMutation.mutateAsync({ key, value })
      }

      queryClient.invalidateQueries({ queryKey: ['settings'] })
      toast.success('Settings saved successfully')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save settings'
      toast.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  // Bank Account handlers
  const handleOpenDialog = (bankAccount?: BankAccount) => {
    if (bankAccount) {
      setEditingBankAccount(bankAccount)
      setBankAccountForm({
        name: bankAccount.name,
        account_number: bankAccount.account_number,
        bank_name: bankAccount.bank_name,
        is_active: bankAccount.is_active,
      })
    } else {
      setEditingBankAccount(null)
      setBankAccountForm(initialBankAccountForm)
    }
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingBankAccount(null)
    setBankAccountForm(initialBankAccountForm)
  }

  const handleSubmitBankAccount = () => {
    if (!bankAccountForm.name || !bankAccountForm.account_number || !bankAccountForm.bank_name) {
      toast.error('Please fill in all required fields')
      return
    }

    if (editingBankAccount) {
      updateBankAccountMutation.mutate({
        id: editingBankAccount.id,
        data: bankAccountForm,
      })
    } else {
      createBankAccountMutation.mutate(bankAccountForm)
    }
  }

  const handleDeleteBankAccount = (bankAccount: BankAccount) => {
    setBankAccountToDelete(bankAccount)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteBankAccount = () => {
    if (bankAccountToDelete) {
      deleteBankAccountMutation.mutate(bankAccountToDelete.id)
    }
  }

  const bankAccounts = bankAccountsResponse?.data || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading settings...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          System Settings
        </h1>
        <p className="text-muted-foreground mt-2">Manage system-wide configuration and settings</p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0 h-auto">
          <TabsTrigger
            value="general"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 data-[state=active]:shadow-none px-6 py-3 font-medium text-muted-foreground data-[state=active]:text-blue-600 transition-colors hover:text-foreground"
          >
            <Building2 className="mr-2 h-4 w-4" />
            General Info
          </TabsTrigger>
          <TabsTrigger
            value="order"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 data-[state=active]:shadow-none px-6 py-3 font-medium text-muted-foreground data-[state=active]:text-blue-600 transition-colors hover:text-foreground"
          >
            <Receipt className="mr-2 h-4 w-4" />
            Order & Tax
          </TabsTrigger>
          <TabsTrigger
            value="social"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 data-[state=active]:shadow-none px-6 py-3 font-medium text-muted-foreground data-[state=active]:text-blue-600 transition-colors hover:text-foreground"
          >
            <Globe className="mr-2 h-4 w-4" />
            Social Media
          </TabsTrigger>
          <TabsTrigger
            value="bank-accounts"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 data-[state=active]:shadow-none px-6 py-3 font-medium text-muted-foreground data-[state=active]:text-blue-600 transition-colors hover:text-foreground"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Bank Accounts
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>Store Information</CardTitle>
                <CardDescription>
                  Details about your store that appear on invoices and contact pages.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="site_title">Site Title (Browser Tab)</Label>
                    <Input
                      id="site_title"
                      value={settings.site_title}
                      onChange={(e) => handleChange('site_title', e.target.value)}
                      placeholder="e.g. ATP | Water Pump E-commerce"
                    />
                    <p className="text-xs text-muted-foreground">
                      This appears in the browser tab and search results.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="store_name">Store Name</Label>
                    <Input
                      id="store_name"
                      value={settings.store_name}
                      onChange={(e) => handleChange('store_name', e.target.value)}
                      placeholder="e.g. Arena Teknik Pompa"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="store_address">Store Address</Label>
                    <Input
                      id="store_address"
                      value={settings.store_address}
                      onChange={(e) => handleChange('store_address', e.target.value)}
                      placeholder="Full physical address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="store_phone">Phone Number</Label>
                      <Input
                        id="store_phone"
                        value={settings.store_phone}
                        onChange={(e) => handleChange('store_phone', e.target.value)}
                        placeholder="e.g. +62 31 3595 4555"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="store_whatsapp">WhatsApp Number</Label>
                      <Input
                        id="store_whatsapp"
                        value={settings.store_whatsapp}
                        onChange={(e) => handleChange('store_whatsapp', e.target.value)}
                        placeholder="e.g. +62822 9706 9757"
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="store_email">Support Email</Label>
                    <Input
                      id="store_email"
                      type="email"
                      value={settings.store_email}
                      onChange={(e) => handleChange('store_email', e.target.value)}
                      placeholder="e.g. support@example.com"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="store_hours">Operation Hours</Label>
                    <Input
                      id="store_hours"
                      value={settings.store_hours}
                      onChange={(e) => handleChange('store_hours', e.target.value)}
                      placeholder="e.g. Senin - Jumat: 08:00 - 17:00"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="store_latitude">Latitude</Label>
                      <Input
                        id="store_latitude"
                        type="number"
                        step="0.0000001"
                        value={settings.store_latitude}
                        onChange={(e) => handleChange('store_latitude', e.target.value)}
                        placeholder="e.g. -7.2898784"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="store_longitude">Longitude</Label>
                      <Input
                        id="store_longitude"
                        type="number"
                        step="0.0000001"
                        value={settings.store_longitude}
                        onChange={(e) => handleChange('store_longitude', e.target.value)}
                        placeholder="e.g. 112.7803816"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button
                  onClick={() => handleSaveTab(GENERAL_KEYS)}
                  disabled={isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="order">
            <Card>
              <CardHeader>
                <CardTitle>Tax Configuration</CardTitle>
                <CardDescription>Configure the tax rate applied to all orders.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tax_rate">Tax Rate (%)</Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.tax_rate}
                    onChange={(e) => handleChange('tax_rate', e.target.value)}
                    placeholder="Enter tax rate (e.g., 10 for 10%)"
                    className="max-w-xs"
                  />
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button
                  onClick={() => handleSaveTab(ORDER_KEYS)}
                  disabled={isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="social">
            <Card>
              <CardHeader>
                <CardTitle>Social Media Links</CardTitle>
                <CardDescription>
                  Links to your social media profiles displayed in the footer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="social_instagram">Instagram URL</Label>
                    <Input
                      id="social_instagram"
                      value={settings.social_instagram}
                      onChange={(e) => handleChange('social_instagram', e.target.value)}
                      placeholder="https://instagram.com/username"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="social_facebook">Facebook URL</Label>
                    <Input
                      id="social_facebook"
                      value={settings.social_facebook}
                      onChange={(e) => handleChange('social_facebook', e.target.value)}
                      placeholder="https://facebook.com/username"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="social_x">X (Twitter) URL</Label>
                    <Input
                      id="social_x"
                      value={settings.social_x}
                      onChange={(e) => handleChange('social_x', e.target.value)}
                      placeholder="https://x.com/username"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="social_youtube">YouTube URL</Label>
                    <Input
                      id="social_youtube"
                      value={settings.social_youtube}
                      onChange={(e) => handleChange('social_youtube', e.target.value)}
                      placeholder="https://youtube.com/@channel"
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t pt-6">
                <Button
                  onClick={() => handleSaveTab(SOCIAL_KEYS)}
                  disabled={isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="bank-accounts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Bank Accounts</CardTitle>
                  <CardDescription>
                    Manage bank accounts for payment transfers. These will be displayed to customers
                    during checkout.
                  </CardDescription>
                </div>
                <Button onClick={() => handleOpenDialog()} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Bank Account
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingBankAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading bank accounts...</span>
                  </div>
                ) : bankAccounts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No bank accounts configured yet.</p>
                    <p className="text-sm">Click &quot;Add Bank Account&quot; to get started.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Bank Name</TableHead>
                        <TableHead>Account Name</TableHead>
                        <TableHead>Account Number</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankAccounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="font-medium">{account.bank_name}</TableCell>
                          <TableCell>{account.name}</TableCell>
                          <TableCell className="font-mono">{account.account_number}</TableCell>
                          <TableCell>
                            <Badge variant={account.is_active ? 'default' : 'secondary'}>
                              {account.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(account)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteBankAccount(account)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      {/* Bank Account Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingBankAccount ? 'Edit Bank Account' : 'Add Bank Account'}
            </DialogTitle>
            <DialogDescription>
              {editingBankAccount
                ? 'Update the bank account details below.'
                : 'Fill in the bank account details for payment transfers.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                value={bankAccountForm.bank_name}
                onChange={(e) =>
                  setBankAccountForm((prev) => ({ ...prev, bank_name: e.target.value }))
                }
                placeholder="e.g. Bank Central Asia (BCA)"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account_name">Account Name *</Label>
              <Input
                id="account_name"
                value={bankAccountForm.name}
                onChange={(e) => setBankAccountForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. PT Arena Teknik Pompa"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account_number">Account Number *</Label>
              <Input
                id="account_number"
                value={bankAccountForm.account_number}
                onChange={(e) =>
                  setBankAccountForm((prev) => ({ ...prev, account_number: e.target.value }))
                }
                placeholder="e.g. 1234567890"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={bankAccountForm.is_active}
                onCheckedChange={(checked) =>
                  setBankAccountForm((prev) => ({ ...prev, is_active: checked }))
                }
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBankAccount}
              disabled={createBankAccountMutation.isPending || updateBankAccountMutation.isPending}
            >
              {(createBankAccountMutation.isPending || updateBankAccountMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingBankAccount ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        _open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Are you sure?"
        description={`This will permanently delete the bank account "${bankAccountToDelete?.bank_name} - ${bankAccountToDelete?.name}". This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteBankAccount}
        isSubmitting={deleteBankAccountMutation.isPending}
        variant="destructive"
      />
    </div>
  )
}
